import dayjs from "dayjs";
import { searchFlights, searchLds } from "./fhpService";
import { normalizeLdsSearchDeals } from "../utils/normalizeLdsSearchDeals";
import { getLdsHotelFromPackage } from "../utils/ldsDealHelpers";
import { pickRandomUnique } from "../utils/vacationPackagesDiscovery/pickRandomUnique";
import {
  mapFlightDealToDiscoveryCard,
  mapLdsDealToDiscoveryCard,
} from "../utils/vacationPackagesDiscovery/mapRawDealsToDiscoveryCards";
import {
  dealsListFromSearchFlightsResponseBody,
  isSearchFlightsAxiosResponseOk,
} from "../utils/flightParallelSearchHelpers";
import { VP_FIXED_DESTINATION_SECTIONS } from "../utils/vacationPackagesDiscovery/vpFixedDestinationSections";

/** כמות כרטיסים מקסימלית למסילת טיסות */
const CARD_COUNT = 8;
const REQ_TIMEOUT_MS = 24000;

const LDS_CITIES = [
  "PAR",
  "ROM",
  "BCN",
  "ATH",
  "LON",
  "AMS",
  "MAD",
  "FCO",
  "PRG",
  "VIE",
  "BER",
  "LIS",
];

const FLIGHT_ROUTES = [
  { origin: "TLV", destination: "ATH" },
  { origin: "TLV", destination: "BCN" },
  { origin: "TLV", destination: "ROM" },
  { origin: "TLV", destination: "PAR" },
  { origin: "TLV", destination: "LON" },
  { origin: "TLV", destination: "AMS" },
];

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const id = setTimeout(() => {
      const err = new Error("Timeout");
      err.code = "TIMEOUT";
      reject(err);
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(id);
        resolve(v);
      },
      (e) => {
        clearTimeout(id);
        reject(e);
      }
    );
  });
}

function defaultTripDates() {
  const next = dayjs().add(1, "month").startOf("month");
  const dep = next.format("DD/MM/YYYY");
  const ret = next.add(7, "day").format("DD/MM/YYYY");
  return { dep, ret };
}

/**
 * מספר חלונות יציאה שונים (הלוך + 7 ימים חזור), כדי שלא כל הכרטיסים יהיו באותו תאריך.
 */
function discoveryFlightDateVariants() {
  const monthStart = dayjs().add(1, "month").startOf("month");
  const weekOffsets = [0, 2, 4, 6];
  return weekOffsets.map((weeks) => {
    const depDay = monthStart.add(weeks * 7, "day");
    return {
      dep: depDay.format("DD/MM/YYYY"),
      ret: depDay.add(7, "day").format("DD/MM/YYYY"),
    };
  });
}

async function fetchFlightsSection() {
  const room_compositions = [
    { adults: 2, kids: 0, infants: 0, kidsAges: [] },
  ];
  const pool = [];
  const dateVariants = discoveryFlightDateVariants();
  const routes = pickRandomUnique(FLIGHT_ROUTES, 6, (r) =>
    `${r.origin}-${r.destination}`
  );

  for (let i = 0; i < routes.length && pool.length < 32; i++) {
    const route = routes[i];
    const { dep, ret } = dateVariants[i % dateVariants.length];
    try {
      const res = await withTimeout(
        searchFlights({
          dealType: "RoundTrip",
          origin: route.origin,
          destination: route.destination,
          dep_date: dep,
          ret_date: ret,
          room_compositions,
        }),
        REQ_TIMEOUT_MS
      );
      if (!isSearchFlightsAxiosResponseOk(res)) continue;
      const deals = dealsListFromSearchFlightsResponseBody(res?.data);
      const meta = { ...route, dep_date: dep, ret_date: ret };
      for (const d of deals) {
        pool.push(mapFlightDealToDiscoveryCard(d, meta));
      }
    } catch {
      /* continue */
    }
  }

  return pickRandomUnique(pool, CARD_COUNT, (c) => c.key);
}

function ldsDealOkForOrganized(deal) {
  const full = deal?.fullDeal || deal;
  return Boolean(
    getLdsHotelFromPackage(deal) ||
      full?.tourName ||
      full?.packageName ||
      full?.tour?.tourName
  );
}

async function fetchLdsSection(isOrganized) {
  const { dep, ret } = defaultTripDates();
  const room_compositions = [
    { adults: 2, kids: 0, infants: 0, kidsAges: [] },
  ];
  const pool = [];
  const cities = pickRandomUnique(LDS_CITIES, 10, (c) => c);
  const BATCH = 3;

  for (let i = 0; i < cities.length && pool.length < 24; i += BATCH) {
    const chunk = cities.slice(i, i + BATCH);
    const settled = await Promise.allSettled(
      chunk.map((city) =>
        withTimeout(
          searchLds({
            dep_date: dep,
            ret_date: ret,
            city,
            categories: isOrganized ? ["OrganizedTour"] : [],
            room_compositions,
          }),
          REQ_TIMEOUT_MS
        )
      )
    );
    for (let j = 0; j < settled.length; j++) {
      const s = settled[j];
      const city = chunk[j];
      if (s.status !== "fulfilled") continue;
      const res = s.value;
      if (!res || (res.status && res.status >= 400)) continue;
      const raw = res.data ?? res;
      const { deals, opError } = normalizeLdsSearchDeals(raw);
      if (opError) continue;
      for (const deal of deals) {
        if (isOrganized) {
          if (!ldsDealOkForOrganized(deal)) continue;
        } else if (!getLdsHotelFromPackage(deal)) continue;
        pool.push(
          mapLdsDealToDiscoveryCard(
            deal,
            city,
            isOrganized ? "organized" : "package"
          )
        );
      }
    }
  }

  return pickRandomUnique(pool, CARD_COUNT, (c) => c.key);
}

/**
 * חבילות LDS (לא מאורגן) ליעד יחיד — לבלוקי יעד בעמוד הבית.
 */
export async function fetchDiscoveryPackagesForCity(cityCode) {
  const code = String(cityCode || "")
    .trim()
    .toUpperCase();
  if (!code) return [];

  const { dep, ret } = defaultTripDates();
  const room_compositions = [
    { adults: 2, kids: 0, infants: 0, kidsAges: [] },
  ];
  const pool = [];

  try {
    const res = await withTimeout(
      searchLds({
        dep_date: dep,
        ret_date: ret,
        city: code,
        categories: [],
        room_compositions,
      }),
      REQ_TIMEOUT_MS
    );
    if (!res || (res.status && res.status >= 400)) return [];
    const raw = res.data ?? res;
    const { deals, opError } = normalizeLdsSearchDeals(raw);
    if (opError) return [];
    for (const deal of deals) {
      if (!getLdsHotelFromPackage(deal)) continue;
      pool.push(mapLdsDealToDiscoveryCard(deal, code, "package"));
    }
  } catch {
    return [];
  }

  if (!pool.length) return [];
  const cap = 24;
  return pickRandomUnique(
    pool,
    Math.min(pool.length, cap),
    (c) => c.key
  );
}

export async function fetchDiscoveryFlightsSection() {
  return fetchFlightsSection();
}

/** @deprecated שימוש פנימי ישן; העדפת fetchDiscoveryPackagesForCity */
export async function fetchDiscoveryPackagesSection() {
  return fetchLdsSection(false);
}

/** @deprecated שימוש פנימי ישן */
export async function fetchDiscoveryOrganizedSection() {
  return fetchLdsSection(true);
}

/**
 * טרום-טעינה / מטמון: טיסות + כל יעדי VP_FIXED_DESTINATION_SECTIONS.
 */
export async function fetchVacationPackagesDiscoveryRails() {
  const flights = await fetchDiscoveryFlightsSection();
  const destLists = await Promise.all(
    VP_FIXED_DESTINATION_SECTIONS.map((d) =>
      fetchDiscoveryPackagesForCity(d.cityCode)
    )
  );
  const out = { flights };
  VP_FIXED_DESTINATION_SECTIONS.forEach((d, i) => {
    out[d.id] = destLists[i] ?? [];
  });
  return out;
}
